import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export enum UserAccessLevel {
	USER = 100,
	VERIZON = 125,
	ADMIN = 500,
	SUPER_ADMIN = 1000,
}

export interface IUser {
	id: number;
	email: string;
	organization_id: number;
	external_firebase_id: string;
	externalFirebaseId: string; // camelCase version
	is_account_disabled: boolean;
	isAccountDisabled: boolean; // camelCase version
	role_id: number;
	roleId: number; // camelCase version
	name: string;
	is_internal_account: boolean;
	role: IRole;
	user_config: IUserConfig;
	organization: IOrganization;
	allowed_organizations: IOrganization[];
	allowedOrganizations: IOrganization[]; // camelCase version
	// Trial-related fields
	is_trial?: boolean;
	isTrial?: boolean; // camelCase version
	trial_expiration_date?: string;
	trialExpirationDate?: string; // camelCase version
	isTrialAccount?: boolean; // Legacy field for compatibility
	trialStartDate?: string; // Legacy field for compatibility
	trialEndDate?: string; // Legacy field for compatibility
	trialStatus?: string; // Legacy field for compatibility
	// Subscription-related fields
	hasPlusSubscription?: boolean; // True if user has Plus subscription or is super admin
	hasActiveSubscription?: boolean; // True if user has any active subscription
	subscriptionStatus?: "active" | "canceled" | "past_due" | "trialing" | null;
}

export interface IForm {
	id?: number;
	form_id?: number;
	formId?: number;
	organization_id?: number;
	organizationId?: number;
	enabled?: boolean;
	config?: any;
	[key: string]: any; // Allow any additional properties
}

export interface IFormConfig {
	formName: string;
	description: string;
	sections: IFormSection[];
}

export interface IFormSection {
	sectionName: string;
	fields: IFormField[];
}

export interface IFormField {
	[key: string]: unknown;
}

export interface IRole {
	access_level: number;
	accessLevel?: number; // camelCase version from backend
	id: number;
	name: string;
}

export interface IUserConfig {
	user_id: number;
	use_camera_on_photo_capture: boolean;
	saved_filters: {
		assetsTable: IAssetsTableFilters;
	};
	profile_picture_url: string | null;
	is_on_email_list: boolean;
}

export interface IAssetsTableFilters {
	address: boolean;
	assetId: boolean;
	make: boolean;
	model: boolean;
	serial: boolean;
	dateOfBirth: boolean;
	subOrg: boolean | null;
	healthscore: boolean;
	submittedDate: boolean;
	warranty: boolean;
	tempAlertId: boolean;
	assetType: boolean;
	assetTypeCategory: boolean;
}

export interface IOrganization {
	full_name: string;
	fullName?: string; // camelCase version
	id: number;
	external_firebase_id: string;
	externalFirebaseId?: string; // camelCase version
	health: IHealth;
	config: IOrganizationConfig;
	forms: IForm[];
	graphs: unknown[];
	reports: IReport[];
}

export interface IMasterReportKey {
	id: number;
	report_name: string;
	description: string | null;
}

export interface IReport {
	report_id: number;
	enabled: boolean;
	organization_id: number;
	master_report_key: IMasterReportKey;
}

export interface IHealth {
	unhealthy_assets: number;
	organization_id: number;
	assets_near_death: number;
	health_score: number;
}

export interface IAssetTypeSettings {
	usefulLife?: number;
	replacementCost?: number;
	installationDeliveryFactor?: number;
	extendedLifeEligible?: boolean;
}

export interface IAssetFamilySettings {
	usefulLife?: number;
	replacementCost?: number;
	installationDeliveryFactor?: number;
	assetTypes?: Record<string, IAssetTypeSettings>;
}

export interface IPredictiveSpendSettings {
	annualInflationRate?: number;
	assetFamilies?: Record<string, IAssetFamilySettings>;
	usefulLifeOverrides?: Record<string, number>; // DEPRECATED
	replacementCostOverrides?: Record<string, number>; // DEPRECATED
}

export interface IOrganizationConfig {
	show_matterport: boolean;
	organization_id: number;
	is_location_name_required: boolean;
	service_channel_export_enabled: boolean;
	org_specific_inputs: Record<string, unknown>;
	sc_export_options: ISCExportOption[];
	unique_fields: IUniqueFields;
	logo_url?: string | null;
	logoUrl?: string | null; // CamelCase version from backend
	show_iguide: boolean;
	auto_save_drafts: boolean;
	allow_tag_id: boolean;
	export_options: IExportOptions;
	integration_settings: Record<string, unknown>;
	predictive_spend_settings?: IPredictiveSpendSettings;
	predictiveSpendSettings?: IPredictiveSpendSettings; // CamelCase version from backend
}

export interface ISCExportOption {
	subtype: string[];
	headers: ISCExportHeader[];
}

export interface ISCExportHeader {
	key: string;
	header: string;
}

export interface IUniqueFields {
	[key: string]: unknown;
}

export interface IExportOptions {
	address1: unknown[];
}

interface UserState {
	user: IUser | null;
	setUser: (user: IUser) => void;
}

export const useUserStore = create<UserState>()(
	persist(
		(set, get) => ({
			user: get()?.user,
			setUser: (user: IUser) => set({ user }),
		}),
		{
			name: "dfp-user",
			storage: createJSONStorage(() => localStorage),
		},
	),
);
